import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { Box, Button, Flex, Select, Spinner, Text, TextField } from "@radix-ui/themes";
import { useState, useMemo } from "react";
import { Control, Controller, FieldErrors, FieldValues, Path } from "react-hook-form";
import styles from "./SelectComponent.module.scss";

interface SelectProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  style?: React.CSSProperties;
  fieldProps?: Record<string, any>;
  options?: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  onSelect?: (value: string) => void;
  composedName?: { parent: string; prop: string };
  required?: boolean;
}

//const MAX_RESULTS = 10;

const SelectSearchableComponent = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  style,
  fieldProps,
  options = [],
  onSelect,
  composedName,
  itemsError,
  itemsLoading,
  required,
}: SelectProps<T>) => {
  const castedErrors = errors as Record<string, any>;
  const composedError =
    composedName?.parent && composedName?.prop
      ? castedErrors?.[composedName.parent]?.[composedName.prop]
      : undefined;

  const [searchTerm, setSearchTerm] = useState("");
  
  const filteredOptions = useMemo(() => {
    return options
      .filter((option) =>
        option.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    //  .slice(0, MAX_RESULTS);
  }, [options, searchTerm]);

  return (
    <Box style={style}>
      <Text as="label" size="2" className={styles.label}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select.Root
            onValueChange={(value) => {
              field.onChange(value);
              onSelect?.(value);
            }}
            value={field.value || ""}
            {...fieldProps}
          >
            <Select.Trigger style={{ width: "100%" }}>
              {options.find((p) => p.value === field.value)?.label || "Selecione uma opção"}
            </Select.Trigger>
            {field.value && !fieldProps?.disabled && (
                      <Button
                        variant="ghost"
                        size="1"
                        onClick={() => field.onChange("")}
                        className={styles.clearButton}
                        style={{ margin: "4px 0px 0px 0px" }}
                      >
                        <Cross2Icon />
                      </Button>
                    )}
            {!itemsLoading && !itemsError && options.length > 0 && (
              <Select.Content
                style={{ maxHeight: "200px",
                  //  minWidth: "250px",
                  //   overflowY: "auto"
                   }}
                position="popper"
              >
                <Select.Group>
                  <Select.Label>Opções</Select.Label>
                  <Flex direction="column" gap="2">
                    <TextField.Root
                      size="2"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Pesquisar"
                    >
                      <TextField.Slot>
                        <MagnifyingGlassIcon height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                    {filteredOptions.length ? (
                      filteredOptions.map((option) => (
                        <Select.Item key={option.value} value={option.value}>
                          {option.label}
                        </Select.Item>
                      ))
                    ) : (
                      <Select.Item disabled value="Nenhuma opção encontrada">
                        Nenhuma opção encontrada
                      </Select.Item>
                    )}
                  </Flex>
                </Select.Group>
              </Select.Content>
            )}
            {itemsLoading && <Spinner loading />}
            {itemsError && <Text color="red">Erro ao carregar itens</Text>}
          </Select.Root>
        )}
      />
      {(composedError || errors?.[name]) && (
        <Text color="red" size="1">
          {composedError?.message || errors?.[name]?.message}
        </Text>
      )}
    </Box>
  );
};

export default SelectSearchableComponent;
